import React, { useEffect, useState } from 'react';
import { apiGet, apiPost, apiPut, apiDel, api } from '../../lib/api';
import { formatDateTime as _fmtDT } from '../../lib/format';
import { toast } from 'sonner';
import { Plus, Play, Download, Trash2, Mail, X, Loader2, Calendar, FileSpreadsheet } from 'lucide-react';

const OPS = ['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'contains', 'in', 'nin'];
const FREQS = ['daily', 'weekly', 'monthly'];
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const blank = {
  name: '',
  source: 'projects',
  columns: [],
  filters: [],
  sort: null,
  schedule: { enabled: false, frequency: 'weekly', day: 0, time_ist: '09:00', recipients: [], format: 'excel' },
};

export default function ReportBuilder() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [schema, setSchema] = useState({ sources: {}, ops: OPS });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(blank);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);   // { name, columns, rows }
  const [busyId, setBusyId] = useState(null);
  const [recipientsInput, setRecipientsInput] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiGet('/mis/saved-reports');
      setItems(r.items || []);
    } catch {
      toast.error('Could not load saved reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    apiGet('/mis/source-schema').then((r) => setSchema(r)).catch(() => {});
  }, []);

  const openNew = () => {
    const src = 'projects';
    setForm({ ...blank, source: src, columns: schema.sources[src] || [] });
    setEditing(null);
    setRecipientsInput('');
    setShowForm(true);
  };

  const openEdit = (it) => {
    setForm({
      name: it.name, source: it.source, columns: it.columns || [],
      filters: it.filters || [], sort: it.sort || null,
      schedule: it.schedule || { ...blank.schedule },
    });
    setRecipientsInput((it.schedule?.recipients || []).join(', '));
    setEditing(it.id);
    setShowForm(true);
  };

  const onSourceChange = (s) => {
    setForm((f) => ({ ...f, source: s, columns: schema.sources[s] || [], filters: [], sort: null }));
  };

  const toggleColumn = (c) => {
    setForm((f) => ({
      ...f,
      columns: f.columns.includes(c) ? f.columns.filter((x) => x !== c) : [...f.columns, c],
    }));
  };

  const addFilter = () => {
    const first = (schema.sources[form.source] || [])[0];
    if (!first) return;
    setForm((f) => ({ ...f, filters: [...f.filters, { field: first, op: 'eq', value: '' }] }));
  };
  const updateFilter = (i, patch) =>
    setForm((f) => ({ ...f, filters: f.filters.map((x, j) => (j === i ? { ...x, ...patch } : x)) }));
  const removeFilter = (i) =>
    setForm((f) => ({ ...f, filters: f.filters.filter((_, j) => j !== i) }));

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (!form.columns.length) { toast.error('Pick at least one column'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        schedule: {
          ...form.schedule,
          recipients: recipientsInput.split(',').map((s) => s.trim()).filter(Boolean),
        },
      };
      if (editing) await apiPut(`/mis/saved-reports/${editing}`, payload);
      else await apiPost('/mis/saved-reports', payload);
      toast.success(editing ? 'Report updated' : 'Report saved');
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const runNow = async (id) => {
    setBusyId(id);
    try {
      const r = await apiPost(`/mis/saved-reports/${id}/run`, {});
      setPreview(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Run failed');
    } finally {
      setBusyId(null);
    }
  };

  const exportExcel = async (id, name) => {
    setBusyId(id);
    try {
      const r = await api.post(`/mis/saved-reports/${id}/export`, {}, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(name || 'report').replace(/[^\w-]+/g, '_')}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this saved report?')) return;
    try {
      await apiDel(`/mis/saved-reports/${id}`);
      toast.success('Deleted');
      load();
    } catch {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="report-builder-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-display">Report Builder</h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Saved reports run on-demand or on a schedule and are emailed as Excel attachments.
          </p>
        </div>
        <button
          onClick={openNew}
          data-testid="new-report-btn"
          className="inline-flex items-center gap-1.5 rounded bg-indigo-700 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-800"
        >
          <Plus size={14} /> New report
        </button>
      </div>

      <div className="bg-white border rounded-lg shadow-sm overflow-hidden">
        <table className="w-full" data-testid="saved-reports-table">
          <thead className="bg-slate-50">
            <tr className="text-xs uppercase tracking-wider text-slate-500">
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Source</th>
              <th className="text-left px-3 py-2">Columns</th>
              <th className="text-left px-3 py-2">Schedule</th>
              <th className="text-left px-3 py-2">Last Run</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="py-6 text-center text-sm text-slate-500">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6} className="py-8 text-center text-sm text-slate-500">No saved reports yet. Click <b>New report</b>.</td></tr>
            ) : items.map((it) => (
              <tr key={it.id} className="border-t hover:bg-slate-50">
                <td className="px-3 py-2">
                  <button onClick={() => openEdit(it)} className="text-sm font-medium text-indigo-700 hover:text-indigo-900">
                    {it.name}
                  </button>
                </td>
                <td className="px-3 py-2 text-sm text-slate-700">{it.source}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{(it.columns || []).length} cols</td>
                <td className="px-3 py-2 text-xs">
                  {it.schedule?.enabled ? (
                    <span className="inline-flex items-center gap-1 text-emerald-700">
                      <Calendar size={11} /> {it.schedule.frequency} @ {it.schedule.time_ist}
                    </span>
                  ) : <span className="text-slate-400">—</span>}
                </td>
                <td className="px-3 py-2 text-xs text-slate-500">
                  {it.last_run_at ? _fmtDT(it.last_run_at) : '—'}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <button
                    onClick={() => runNow(it.id)} disabled={busyId === it.id}
                    data-testid={`run-report-${it.id}`}
                    className="inline-flex items-center gap-1 text-xs text-indigo-700 hover:text-indigo-900 mr-3"
                  >
                    {busyId === it.id ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />} Run
                  </button>
                  <button
                    onClick={() => exportExcel(it.id, it.name)} disabled={busyId === it.id}
                    className="inline-flex items-center gap-1 text-xs text-slate-700 hover:text-slate-900 mr-3"
                  >
                    <Download size={12} /> Excel
                  </button>
                  <button onClick={() => remove(it.id)} className="text-rose-600 hover:text-rose-800">
                    <Trash2 size={12} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Builder drawer */}
      {showForm && (
        <div className="fixed inset-0 bg-black/30 z-50 flex justify-end" onClick={() => setShowForm(false)}>
          <div
            className="w-full max-w-2xl bg-white shadow-xl overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="report-form-drawer"
          >
            <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between">
              <h2 className="font-bold text-slate-900">{editing ? 'Edit report' : 'New report'}</h2>
              <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Name</label>
                <input
                  data-testid="report-name-input"
                  value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
                  placeholder="e.g. Overdue vendor invoices"
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Source</label>
                <select
                  data-testid="report-source-select"
                  value={form.source} onChange={(e) => onSourceChange(e.target.value)}
                  className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm bg-white"
                >
                  {Object.keys(schema.sources || {}).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Columns</label>
                <div className="flex flex-wrap gap-2">
                  {(schema.sources[form.source] || []).map((c) => (
                    <label key={c} className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded border cursor-pointer ${form.columns.includes(c) ? 'bg-indigo-50 border-indigo-300 text-indigo-800' : 'bg-white border-slate-300 text-slate-700'}`}>
                      <input
                        type="checkbox" className="accent-indigo-600"
                        checked={form.columns.includes(c)} onChange={() => toggleColumn(c)}
                      />
                      {c}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Filters</label>
                  <button onClick={addFilter} className="text-xs text-indigo-700 hover:text-indigo-900">+ add filter</button>
                </div>
                <div className="space-y-2">
                  {(form.filters || []).map((f, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                      <select
                        value={f.field} onChange={(e) => updateFilter(i, { field: e.target.value })}
                        className="border border-slate-300 rounded px-1.5 py-1 text-xs bg-white flex-1"
                      >
                        {(schema.sources[form.source] || []).map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <select
                        value={f.op} onChange={(e) => updateFilter(i, { op: e.target.value })}
                        className="border border-slate-300 rounded px-1.5 py-1 text-xs bg-white"
                      >
                        {(schema.ops || OPS).map((op) => <option key={op} value={op}>{op}</option>)}
                      </select>
                      <input
                        value={f.value ?? ''} onChange={(e) => updateFilter(i, { value: e.target.value })}
                        className="border border-slate-300 rounded px-1.5 py-1 text-xs flex-1"
                        placeholder="value"
                      />
                      <button onClick={() => removeFilter(i)} className="text-rose-600 hover:text-rose-800"><X size={14} /></button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Sort</label>
                <div className="flex items-center gap-1.5">
                  <select
                    value={form.sort?.field || ''}
                    onChange={(e) => setForm({ ...form, sort: e.target.value ? { field: e.target.value, dir: form.sort?.dir || 'asc' } : null })}
                    className="border border-slate-300 rounded px-1.5 py-1 text-xs bg-white flex-1"
                  >
                    <option value="">— no sort —</option>
                    {(schema.sources[form.source] || []).map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <select
                    value={form.sort?.dir || 'asc'}
                    onChange={(e) => setForm({ ...form, sort: form.sort ? { ...form.sort, dir: e.target.value } : null })}
                    disabled={!form.sort?.field}
                    className="border border-slate-300 rounded px-1.5 py-1 text-xs bg-white"
                  >
                    <option value="asc">asc</option>
                    <option value="desc">desc</option>
                  </select>
                </div>
              </div>

              {/* Schedule */}
              <div className="border-t pt-4">
                <label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-800">
                  <input
                    type="checkbox" className="accent-indigo-600"
                    data-testid="schedule-enabled-toggle"
                    checked={form.schedule.enabled}
                    onChange={(e) => setForm({ ...form, schedule: { ...form.schedule, enabled: e.target.checked } })}
                  />
                  <Mail size={14} /> Email this report on a schedule
                </label>
                {form.schedule.enabled && (
                  <div className="mt-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <select
                        value={form.schedule.frequency}
                        onChange={(e) => setForm({ ...form, schedule: { ...form.schedule, frequency: e.target.value } })}
                        className="border border-slate-300 rounded px-1.5 py-1 text-xs bg-white"
                      >
                        {FREQS.map((f) => <option key={f} value={f}>{f}</option>)}
                      </select>
                      {form.schedule.frequency === 'weekly' && (
                        <select
                          value={form.schedule.day}
                          onChange={(e) => setForm({ ...form, schedule: { ...form.schedule, day: parseInt(e.target.value) } })}
                          className="border border-slate-300 rounded px-1.5 py-1 text-xs bg-white"
                        >
                          {WEEKDAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
                        </select>
                      )}
                      {form.schedule.frequency === 'monthly' && (
                        <input
                          type="number" min="1" max="28" value={form.schedule.day || 1}
                          onChange={(e) => setForm({ ...form, schedule: { ...form.schedule, day: parseInt(e.target.value) || 1 } })}
                          className="border border-slate-300 rounded px-1.5 py-1 text-xs w-20"
                          placeholder="day"
                        />
                      )}
                      <input
                        type="time" value={form.schedule.time_ist}
                        onChange={(e) => setForm({ ...form, schedule: { ...form.schedule, time_ist: e.target.value } })}
                        className="border border-slate-300 rounded px-1.5 py-1 text-xs"
                      />
                      <span className="text-xs text-slate-500">IST</span>
                    </div>
                    <input
                      value={recipientsInput} onChange={(e) => setRecipientsInput(e.target.value)}
                      placeholder="recipients (comma-separated emails)"
                      className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
                    />
                  </div>
                )}
              </div>

              <div className="pt-4 border-t flex justify-end gap-2">
                <button onClick={() => setShowForm(false)} className="px-3 py-1.5 text-sm text-slate-700 hover:text-slate-900">Cancel</button>
                <button
                  onClick={submit} disabled={saving}
                  data-testid="report-save-btn"
                  className="inline-flex items-center gap-1.5 rounded bg-indigo-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-800 disabled:opacity-60"
                >
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <FileSpreadsheet size={14} />}
                  {editing ? 'Update report' : 'Save report'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview drawer */}
      {preview && (
        <div className="fixed inset-0 bg-black/30 z-50 flex justify-end" onClick={() => setPreview(null)}>
          <div className="w-full max-w-4xl bg-white shadow-xl overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between">
              <h2 className="font-bold text-slate-900">{preview.name} — {preview.row_count} rows</h2>
              <button onClick={() => setPreview(null)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
            </div>
            <div className="overflow-auto flex-1">
              <table className="w-full text-xs" data-testid="report-preview-table">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    {(preview.columns || []).map((c) => (
                      <th key={c} className="text-left px-2 py-1.5 font-bold uppercase tracking-wider text-slate-500 border-b">{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(preview.rows || []).slice(0, 50).map((row, i) => (
                    <tr key={i} className="border-b last:border-0 hover:bg-slate-50">
                      {(preview.columns || []).map((c) => (
                        <td key={c} className="px-2 py-1 text-slate-700 truncate max-w-xs">{String(row[c] ?? '')}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="border-t px-5 py-3 text-xs text-slate-500">
              Showing first 50 rows. Use <b>Excel</b> for the full export (up to 5,000 rows).
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
